import React from 'react';
import {
  SuggestBox,
  ReactDatePicker,
  ReactDateTimePicker,
} from '@mummssoftware/common-ui';
import {
  SuggestBoxWrapper,
  ExpandableSuggestBoxWrapper,
  ErrorText,
} from '../../components/layout/WrapperDiv';
import { boxWidth, physWidth } from './constants';
import defaults from '../../translations/en.json';

type VerbalPickerProps = {
  selectedDate: Date | number | null;
  handleVerbalDateChange: (verbaldate: any) => void;
  severity?: mumms.Severity;
  minimumDate?: Date | number;
  maximumDate?: Date | number;
  width?: number;
  unsetPopperStyles?: boolean;
};

export const VerbalPicker = ({
  selectedDate,
  handleVerbalDateChange,
  severity,
  minimumDate,
  maximumDate,
  width,
  unsetPopperStyles,
}: VerbalPickerProps) => (
  <SuggestBoxWrapper
    severity={severity}
    width={width || boxWidth}
    textAlign="left"
    id="verbal-picker"
  >
    <ReactDatePicker
      popperPosition={unsetPopperStyles ? 'fixed' : ''}
      popperPlacement={unsetPopperStyles ? 'top' : undefined}
      inputMargin="2px"
      selectedDate={selectedDate}
      onChange={handleVerbalDateChange}
      minimumDate={minimumDate}
      maximumDate={maximumDate || new Date()}
      id="progress-notes-verbal-date-picker"
    />
  </SuggestBoxWrapper>
);

type EffDatePickerProps = {
  selectedDate: Date | number | null;
  handleEffDateChange: (effdate: any) => void;
  severity?: mumms.Severity;
  minimumDate?: Date | number | null;
  maximumDate?: Date | number | null;
  width?: number;
  unsetPopperStyles?: boolean;
  error?: string;
  disabled?: boolean;
};

export const EffDatePicker = ({
  selectedDate,
  handleEffDateChange,
  severity,
  width,
  minimumDate,
  maximumDate,
  unsetPopperStyles,
  error,
  disabled,
}: EffDatePickerProps) => (
  <SuggestBoxWrapper
    severity={severity}
    width={width || boxWidth}
    textAlign="left"
  >
    <ReactDatePicker
      inputMargin="2px"
      popperPosition={unsetPopperStyles ? 'fixed' : ''}
      popperPlacement={unsetPopperStyles ? 'top' : undefined}
      selectedDate={selectedDate}
      onChange={handleEffDateChange}
      minimumDate={minimumDate}
      maximumDate={maximumDate}
      id="progress-notes-eff-date-picker"
      disabled={disabled}
    />
    {!!error && <ErrorText>{error}</ErrorText>}
  </SuggestBoxWrapper>
);

export const EffDateTimePicker = ({
  selectedDate,
  handleEffDateChange,
  severity,
  width,
  minimumDate,
  maximumDate,
  unsetPopperStyles,
}: EffDatePickerProps) => (
  <SuggestBoxWrapper severity={severity} width={width}>
    <ReactDateTimePicker
      popperPosition={unsetPopperStyles ? 'fixed' : ''}
      popperPlacement={unsetPopperStyles ? 'top' : undefined}
      inputWidth="140px"
      inputMargin="2px"
      selectedDate={selectedDate}
      onChange={handleEffDateChange}
      minimumDate={minimumDate}
      maximumDate={maximumDate}
      minimumTime={minimumDate || undefined}
      maximumTime={maximumDate || undefined}
      id="progress-notes-date-picker"
    />
  </SuggestBoxWrapper>
);

type PhysicianPickerProps =
  | {
      doctor: mumms.Doctor;
      doctorsOptions: mumms.Doctor[];
      handlePhysicianChange: (doctor: any) => void;
      severity: mumms.Severity;
      maxMenuHeight: number;
    }
  | any;

export const PhysicianPicker = ({
  doctor,
  doctorsOptions,
  handlePhysicianChange,
  severity,
  maxMenuHeight,
  ...props
}: PhysicianPickerProps) => (
  <ExpandableSuggestBoxWrapper severity={severity} width={physWidth}>
    <SuggestBox
      inputId="physician-notes"
      editablePlaceholder
      fitcontent
      placeholder={defaults.physician}
      options={doctorsOptions as mumms.Doctor[]}
      onChange={handlePhysicianChange}
      value={doctor as mumms.Doctor}
      menuPlacement="top"
      noMaxHeight
      maxMenuHeight={maxMenuHeight || 300}
      {...props}
    />
  </ExpandableSuggestBoxWrapper>
);

type RoleGroupPickerProps =
  | {
      roleGroup: mumms.RoleGroup;
      roleGroupOptions: mumms.RoleGroup[];
      handleRoleGroupChange: (roleGroup: any) => void;
      severity: mumms.Severity;
    }
  | any;

export const RoleGroupPicker = ({
  roleGroup,
  roleGroupOptions,
  handleRoleGroupChange,
  severity,
  width,
  ...props
}: RoleGroupPickerProps) => (
  <SuggestBoxWrapper severity={severity} width={width || boxWidth}>
    <SuggestBox
      inputId="role-group-picker"
      editablePlaceholder
      fitcontent
      placeholder={defaults.roleGroup}
      options={roleGroupOptions as mumms.RoleGroup[]}
      onChange={handleRoleGroupChange}
      value={roleGroup as mumms.RoleGroup}
      {...props}
    />
  </SuggestBoxWrapper>
);

type FrequencyPickerProps =
  | {
      frequency: mumms.OptionType;
      frequencyOptions: mumms.OptionType[];
      handleFrequencyChange: (frequency: any) => void;
      severity: mumms.Severity;
    }
  | any;

export const FrequencyPicker = ({
  frequency,
  frequencyOptions,
  handleFrequencyChange,
  severity,
  width,
  ...props
}: FrequencyPickerProps) => (
  <SuggestBoxWrapper severity={severity} width={width || boxWidth}>
    <SuggestBox
      inputId="role-group-frequency"
      editablePlaceholder
      fitcontent
      placeholder={defaults.frequency}
      options={frequencyOptions as mumms.OptionType[]}
      onChange={handleFrequencyChange}
      value={frequency as mumms.OptionType}
      {...props}
    />
  </SuggestBoxWrapper>
);

type PeriodPickerProps =
  | {
      period: mumms.OptionType;
      periodOptions: mumms.OptionType[];
      handlePeriodChange: (period: any) => void;
      severity: mumms.Severity;
    }
  | any;

export const PeriodPicker = ({
  period,
  periodOptions,
  handlePeriodChange,
  severity,
  width,
  ...props
}: PeriodPickerProps) => (
  <SuggestBoxWrapper severity={severity} width={width || boxWidth}>
    <SuggestBox
      inputId="role-group-period"
      editablePlaceholder
      fitcontent
      placeholder={defaults.period}
      options={periodOptions as mumms.OptionType[]}
      onChange={handlePeriodChange}
      value={period as mumms.OptionType}
      {...props}
    />
  </SuggestBoxWrapper>
);

type LOCPickerProps =
  | {
      value: mumms.OptionType | undefined;
      options: mumms.OptionType[];
      handleLocTypeChange: (loc: any) => void;
      severity: mumms.Severity;
      width?: number;
    }
  | any;

export const LOCPicker = ({
  value,
  options,
  handleLocTypeChange,
  severity,
  width,
  ...props
}: LOCPickerProps) => (
  <SuggestBoxWrapper severity={severity} width={width || boxWidth}>
    <SuggestBox
      inputId="admit-loc"
      editablePlaceholder
      fitcontent
      placeholder={defaults.chooseloc}
      options={options}
      onChange={handleLocTypeChange}
      value={value}
      {...props}
    />
  </SuggestBoxWrapper>
);

type FacilityPickerProps =
  | {
      value: mumms.OptionType | undefined;
      options: mumms.OptionType[];
      handleFacilityChange: (fac: any) => void;
      severity: mumms.Severity;
      maxMenuHeight: number;
      width?: number;
    }
  | any;

export const FacilityPicker = ({
  value,
  options,
  handleFacilityChange,
  severity,
  maxMenuHeight,
  width,
  ...props
}: FacilityPickerProps) => (
  <ExpandableSuggestBoxWrapper severity={severity} width={width || 230}>
    <SuggestBox
      menuPlacement="top"
      noMaxHeight
      inputId="facility-name"
      editablePlaceholder
      fitcontent
      placeholder={defaults.facility}
      options={options}
      onChange={handleFacilityChange}
      value={value}
      maxMenuHeight={maxMenuHeight || 300}
      {...props}
    />
  </ExpandableSuggestBoxWrapper>
);

type DischargePickerProps = {
  value: mumms.OptionType | undefined;
  options: mumms.OptionType[];
  handleDischargeChange: (reason: any) => void;
  severity: mumms.Severity;
};

export const DischargePicker = ({
  value,
  options,
  handleDischargeChange,
  severity,
}: DischargePickerProps) => (
  <SuggestBoxWrapper severity={severity} width={boxWidth}>
    <SuggestBox
      inputId="discharge-reason"
      editablePlaceholder
      fitcontent
      placeholder={defaults.dischargeReason}
      options={options}
      onChange={handleDischargeChange}
      value={value}
    />
  </SuggestBoxWrapper>
);
type ConversationPickerProps = {
  value: mumms.OptionType | undefined;
  options: mumms.OptionType[];
  handleConversationChange: (reason: any) => void;
  severity?: mumms.Severity;
};

export const ConversationPicker = ({
  value,
  options,
  handleConversationChange,
  severity,
}: ConversationPickerProps) => (
  <SuggestBoxWrapper width={130}>
    <SuggestBox
      inputId="conversation-type"
      editablePlaceholder
      fitcontent
      placeholder={value}
      options={options}
      onChange={handleConversationChange}
      value={value}
    />
  </SuggestBoxWrapper>
);
